import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtTime, fmtDate, SESSION_STATUS_STYLES } from "@/lib/helpers";
import { PersonSimpleRun, CalendarBlank, X, MagnifyingGlass, Receipt, Timer } from "@phosphor-icons/react";
import ExportBar from "@/components/attendance/ExportBar";

const todayISO = () => new Date().toISOString().slice(0, 10);

// iter-66d — Compute the *displayed* duration of a session, mirroring the
// same formula the backend uses when completing a session
// (sessions_routes.complete_session):
//     duration_min = (ended - started - paused_seconds) / 60
// For in-progress rows we clock against `now`; for paused rows we
// freeze at `paused_at`; for scheduled rows we return null (no duration
// yet). The result is what a Session Log card renders as a green pill
// on its right edge.
function computeSessionDurationMin(s, nowMs = Date.now()) {
  if (!s || !s.started_at) return null;
  const startedMs = new Date(s.started_at).getTime();
  if (Number.isNaN(startedMs)) return null;
  let endMs;
  if (s.status === "completed" && s.ended_at) {
    endMs = new Date(s.ended_at).getTime();
  } else if (s.status === "paused" && s.paused_at) {
    endMs = new Date(s.paused_at).getTime();
  } else if (s.status === "in_progress") {
    endMs = nowMs;
  } else {
    return null; // scheduled or cancelled — nothing to render
  }
  const paused = Number(s.paused_seconds || 0);
  const secs = Math.max(0, Math.round((endMs - startedMs) / 1000) - paused);
  return Math.max(0, Math.round(secs / 60));
}

function fmtSessionDuration(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  if (m === 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export default function SessionsListPage() {
  const { activeBranch, activeRole, user } = useAuth();
  const [items, setItems] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [students, setStudents] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");

  // Selection state for the draft-invoice flow. Only rows with status
  // "completed" can be selected — invoicing works on completed work only.
  const [selected, setSelected] = useState(() => new Set());
  const [groupByType, setGroupByType] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const canDraft = ["owner", "admin", "reception", "accountant"].includes(activeRole);

  const navigate = useNavigate();

  const branchId = activeBranch === "all" ? null : activeBranch;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (branchId) params.branch_id = branchId;
        if (status !== "all") params.status = status;
        if (activeRole === "therapist" && user?.id) params.therapist_id = user.id;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const [s, tt, st, th] = await Promise.all([
          api.get("/sessions", { params }),
          api.get("/therapy-types"),
          api.get("/students", { params: branchId ? { branch_id: branchId } : {} }),
          api.get("/users/therapists"),
        ]);
        setItems(s.data || []);
        setTherapyTypes(tt.data || []);
        setStudents(st.data || []);
        setTherapists(th.data || []);
        // Any filter change wipes the selection so we never accidentally
        // draft an invoice for rows the user can no longer see.
        setSelected(new Set());
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [activeBranch, status, activeRole, dateFrom, dateTo]); // eslint-disable-line

  const nameOfStudent = (id) => students.find(s => s.id === id)?.name || "—";
  // iter-67 — Prefix each student name with its Student ID when
  // present, so the Session Log matches the "SUN-0001 · Aarav" format
  // shown across the app.
  const labelOfStudent = (id) => {
    const s = students.find(x => x.id === id);
    if (!s) return "—";
    return s.student_id ? `${s.student_id} · ${s.name}` : s.name;
  };
  const nameOfTherapist = (id) => therapists.find(t => t.id === id)?.name || "—";
  const ttOf = (id) => therapyTypes.find(t => t.id === id);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(s => {
      const studentNames = (s.student_ids || []).map(id => nameOfStudent(id).toLowerCase()).join(" ");
      // Match against Student IDs too — user can paste SUN-0001 into search.
      const studentIds = (s.student_ids || [])
        .map(id => students.find(x => x.id === id)?.student_id || "")
        .join(" ").toLowerCase();
      const therapistName = nameOfTherapist(s.therapist_id).toLowerCase();
      return studentNames.includes(q) || studentIds.includes(q) || therapistName.includes(q);
    });
  }, [items, search, students, therapists]); // eslint-disable-line

  // Only completed rows are selectable — invoicing works on completed work.
  const selectableIds = useMemo(
    () => filtered.filter(s => s.status === "completed").map(s => s.id),
    [filtered],
  );

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllVisible = () => {
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach(id => next.delete(id));
      } else {
        selectableIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const selectedList = useMemo(
    () => filtered.filter(s => selected.has(s.id)),
    [filtered, selected],
  );

  // For enabling the Draft button — every selected row must belong to the
  // SAME single student.
  const studentIdsInSelection = useMemo(() => {
    const s = new Set();
    for (const row of selectedList) {
      for (const sid of (row.student_ids || [])) s.add(sid);
    }
    return s;
  }, [selectedList]);
  const singleStudent = studentIdsInSelection.size === 1;
  const singleStudentId = singleStudent ? [...studentIdsInSelection][0] : null;
  const draftEnabled = selectedList.length > 0 && singleStudent && canDraft;

  const clearFilters = () => {
    setDateFrom(""); setDateTo(""); setSearch(""); setStatus("all");
    setSelected(new Set());
  };
  const hasFilters = !!(dateFrom || dateTo || search || status !== "all");
  const allVisibleSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));

  const draftInvoice = async () => {
    if (!draftEnabled) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/billing/invoices/from-sessions", {
        session_ids: [...selected],
        group_by_type: groupByType,
      });
      const skipped = (data.skipped_no_rate || []).length;
      if (skipped > 0) {
        toast.warning(`Draft created. ${skipped} session(s) skipped — no rate configured on their therapy type. Add them manually in Billing if needed.`);
      } else {
        toast.success("Draft invoice created");
      }
      navigate(`/billing/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrafting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-1.5">Sessions</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Session log</h1>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="sess-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="sess-to" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-[11px] text-muted-foreground">Search by student or therapist name</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Type a name…" className="pl-9" data-testid="sess-search" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} data-testid="sess-today-btn">
                <CalendarBlank size={14} weight="duotone" /> Today
              </Button>
              {hasFilters && (
                <Button size="sm" variant="ghost" className="gap-1.5" onClick={clearFilters} data-testid="sess-clear-btn">
                  <X size={14} weight="bold" /> Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="in_progress">In progress</TabsTrigger>
          <TabsTrigger value="paused">Paused</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-xs text-muted-foreground" data-testid="sess-match-count">
          {filtered.length} matching session{filtered.length === 1 ? "" : "s"}
        </div>
        <ExportBar
          endpoint="/exports/sessions"
          params={{
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            status: status !== "all" ? status : undefined,
            branch_id: activeBranch && activeBranch !== "all" ? activeBranch : undefined,
            name_search: search || undefined,
            all_sessions: (!dateFrom && !dateTo) ? true : undefined,
          }}
          matchCount={filtered.length}
          emptyMessage="No matching sessions to export"
          testIdPrefix="export-sessions"
        />
      </div>

      {/* Selection action bar — only shows once there is at least one selection */}
      {canDraft && selected.size > 0 && (
        <Card className="border-primary/40 bg-primary/5 sticky top-0 z-20">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0 text-sm">
              <span className="font-medium">{selected.size} selected</span>
              {singleStudent ? (
                <> · Student: <span className="font-medium">{nameOfStudent(singleStudentId)}</span></>
              ) : (
                <span className="text-amber-700"> · {studentIdsInSelection.size} different students — draft is disabled. Pick sessions for a single student.</span>
              )}
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={groupByType} onCheckedChange={v => setGroupByType(!!v)} data-testid="draft-group" />
              Group by therapy type
            </label>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="draft-clear">
              Clear selection
            </Button>
            <Button
              size="sm"
              onClick={draftInvoice}
              disabled={!draftEnabled || drafting}
              className="gap-1.5"
              data-testid="draft-invoice-btn"
            >
              <Receipt size={14} weight="duotone" />
              {drafting ? "Drafting…" : "Draft invoice"}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
       filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <PersonSimpleRun size={32} className="text-muted-foreground/40 mx-auto mb-2" weight="duotone" />
          <div className="text-sm text-muted-foreground">No sessions match these filters.</div>
        </CardContent></Card>
       ) : (
        <Card><CardContent className="p-0">
          <div className="flex items-center gap-3 px-5 py-2 border-b border-border bg-muted/30 text-xs text-muted-foreground">
            {canDraft && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allVisibleSelected}
                  disabled={selectableIds.length === 0}
                  onCheckedChange={toggleAllVisible}
                  aria-label="Select all completed"
                  data-testid="select-all-completed"
                />
                <span className="hidden sm:inline">Select all completed</span>
              </div>
            )}
            <div className="flex-1">{filtered.length} session{filtered.length !== 1 ? "s" : ""} shown</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map(s => {
              const tt = ttOf(s.therapy_type_id);
              const isCompleted = s.status === "completed";
              const isSelected = selected.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`w-full px-5 py-3 flex items-center gap-3 hover:bg-muted/40 ${isSelected ? "bg-primary/5" : ""}`}
                  data-testid={`session-row-${s.id}`}
                >
                  {canDraft && (
                    <Checkbox
                      checked={isSelected}
                      disabled={!isCompleted}
                      onCheckedChange={() => toggleRow(s.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={isCompleted ? "Select for invoice" : "Only completed sessions can be invoiced"}
                      data-testid={`select-session-${s.id}`}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/sessions/${s.id}`)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className="h-9 w-9 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: tt?.color || "#4A6753" }}>
                      {tt?.code || "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.student_ids?.map(labelOfStudent).join(", ") || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate">{nameOfTherapist(s.therapist_id)} · {fmtDate(s.created_at)} · {fmtTime(s.started_at || s.created_at)}</div>
                    </div>
                    {(() => {
                      // iter-66d — Total-time pill. Matches the same
                      // duration formula the backend writes onto the
                      // queue's completed_therapies badge and the
                      // Therapist Queue "last session" chip.
                      const mins = computeSessionDurationMin(s);
                      if (mins == null) return null;
                      const live = s.status === "in_progress";
                      return (
                        <span
                          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums border ${live ? "border-teal-200 bg-teal-50 text-teal-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
                          data-testid={`session-duration-${s.id}`}
                          title={live ? "Live elapsed time" : "Total session duration"}
                        >
                          <Timer size={11} weight="duotone" />
                          {fmtSessionDuration(mins)}
                        </span>
                      );
                    })()}
                    <Badge className={SESSION_STATUS_STYLES[s.status]}>{s.status.replace("_", " ")}</Badge>
                  </button>
                </div>
              );
            })}
          </div>
        </CardContent></Card>
       )}
    </div>
  );
}
